import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';

import { CONFIG } from 'src/environments/environment';

import type { UpdatePolicyState } from 'src/types/update-policy';

import {
  DeviceType,
  SystemStoreType,
  BleReaderStatus,
  BlePaymentStatus,
  UpdateHistoryItem,
  TerminalConnectionStatus,
} from './types';

// Determine platform at module load time for default values
const isNativePlatform = Capacitor.isNativePlatform();

export const useSystemStore = create<SystemStoreType>((set, get) => ({
  shouldRefetchPreload: false,
  baseUrl: CONFIG.VITE_HOST_API,
  baseAthUrl: CONFIG.VITE_ATH_API,
  basePublicUrl: CONFIG.VITE_PUBLIC_API,
  currentDeviceType: DeviceType.DESKTOP,
  appsToDisplayOnMobile: [],
  appVersion: {
    // Web platforms default to 'Latest' (normalized to '999.999.999' by getMetaHeaders)
    // Native platforms start with '0.0.0' and get updated by PreAuthVersionGuard
    current: isNativePlatform ? '0.0.0' : '22.22.22',
    minimum: isNativePlatform ? '0.0.0' : '22.22.22',
    native: undefined,
  },
  currentDevice: {
    id: '',
    name: '',
    type: DeviceType.DESKTOP,
    initialized: false,
  },
  featureFlags: {
    elevateAiEnabled: false,
  },
  devices: [],
  terminals: [],
  currentTerminal: null,
  executionMode: null,
  // Terminal connection initial state
  terminalConnection: {
    status: TerminalConnectionStatus.DISCONNECTED,
    lastConnected: null,
    lastError: null,
  },
  // BLE Reader initial state
  bleReaderStatus: BleReaderStatus.NOT_INITIALIZED,
  bleReader: null,
  bleReaderBattery: {
    level: null,
    isCharging: false,
    status: 'unknown',
  },
  blePaymentStatus: BlePaymentStatus.IDLE,
  isPrinting: false,
  usesBleReader: false,
  // Update-related initial state
  updateStatus: 'idle',
  availableUpdate: null,
  downloadProgress: 0,
  updateHistory: [],
  lastUpdateCheck: null,
  updateError: null,
  // Update policy state
  updatePolicy: {
    globalPolicy: null,
    businessPolicy: null,
    mergedPolicy: null,
    loading: false,
    error: null,
    lastFetch: null,
  },
  // Developer toggle for V4 payment redirect (in-memory only)
  useV4Payment: true,
  // Payment page debug mode toggle (in-memory only)
  showPaymentDebugMode: false,
  // Payment flow state for Footer visibility (in-memory only)
  paymentFlowState: null,
  // Quick Pay panel open state (in-memory only)
  v4QuickPayPanelOpen: false,

  // Existing setters
  setFeatureFlags: (value) => set({ featureFlags: value }),
  setAppVersion: (value) => set({ appVersion: value }),
  setBaseUrl: (value) => set((state) => ({ baseUrl: value.length ? value : state.baseUrl })),
  setBaseAthUrl: (value) =>
    set((state) => ({ baseAthUrl: value.length ? value : state.baseAthUrl })),
  setBasePublicUrl: (value) =>
    set((state) => ({ basePublicUrl: value.length ? value : state.basePublicUrl })),
  setShouldRefetchPreload: (value) => set({ shouldRefetchPreload: value }),
  setCurrentDeviceType: (value) => set({ currentDeviceType: value }),
  setCurrentDevice: (value) => set({ currentDevice: value }),
  setDevices: (value) => set({ devices: value }),
  setTerminals: (value) => set({ terminals: value }),
  setAppsToDisplayOnMobile: (value) => set({ appsToDisplayOnMobile: value }),
  // BLE Reader setters
  setBleReaderStatus: (value) => set({ bleReaderStatus: value }),
  setBleReader: (value) => set({ bleReader: value }),
  setBleReaderBattery: (value) => set({ bleReaderBattery: value }),
  setBlePaymentStatus: (value) => set({ blePaymentStatus: value }),
  setIsPrinting: (value) => set({ isPrinting: value }),
  setUsesBleReader: (value) => set({ usesBleReader: value }),
  // Update-related setters
  setUpdateStatus: (value) => set({ updateStatus: value }),
  setAvailableUpdate: (value) => set({ availableUpdate: value }),
  setDownloadProgress: (value) => set({ downloadProgress: value }),
  setUpdateHistory: (value) => set({ updateHistory: value }),
  setLastUpdateCheck: (value) => set({ lastUpdateCheck: value }),
  setUpdateError: (value) => set({ updateError: value }),
  addToUpdateHistory: (item: UpdateHistoryItem) =>
    set((state) => ({
      updateHistory: [item, ...state.updateHistory].slice(0, 10), // Keep only last 10 entries
    })),
  // Update policy setters
  setUpdatePolicy: (value: Partial<UpdatePolicyState>) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, ...value },
    })),
  setGlobalPolicy: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, globalPolicy: value },
    })),
  setBusinessPolicy: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, businessPolicy: value },
    })),
  setMergedPolicy: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, mergedPolicy: value },
    })),
  setUpdatePolicyLoading: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, loading: value },
    })),
  setUpdatePolicyError: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, error: value },
    })),
  setUseV4Payment: (value) => set({ useV4Payment: value }),
  setShowPaymentDebugMode: (value) => set({ showPaymentDebugMode: value }),
  setPaymentFlowState: (value) => set({ paymentFlowState: value }),
  setV4QuickPayPanelOpen: (value) => set({ v4QuickPayPanelOpen: value }),
  setCurrentTerminal: (value) => set({ currentTerminal: value }),
  setExecutionMode: (value) => {
    set({ executionMode: value });
    // Reset terminal connection when switching execution modes
    get().resetTerminalConnection();
  },
  // Terminal connection setters
  setTerminalConnectionStatus: (status) =>
    set((state) => ({
      terminalConnection: {
        ...state.terminalConnection,
        status,
      },
    })),
  setTerminalConnectionLastConnected: (date) =>
    set((state) => ({
      terminalConnection: {
        ...state.terminalConnection,
        lastConnected: date,
      },
    })),
  setTerminalConnectionError: (error) =>
    set((state) => ({
      terminalConnection: {
        ...state.terminalConnection,
        lastError: error,
      },
    })),
  resetTerminalConnection: () =>
    set({
      terminalConnection: {
        status: TerminalConnectionStatus.DISCONNECTED,
        lastConnected: null,
        lastError: null,
      },
    }),
}));

/**
 *

  const { example, exampleVar: false } = useExampleDataStore((state) => ({
    currentInventory: state.currentInventory,
    newInventory: state.newInventory,
  }));

  const setExample = useExampleDataStore.getState().setExample();

 */
